/**
 * What a file IS, decided on its first bytes rather than its name.
 *
 * ---- Why not the extension ----
 *
 * Suppliers and customers rename files. A `.xls` that is really an `.xlsx`, an `.xlsm` the server
 * recorded with `file_type: null`, a `.doc` that is a Word 97 binary — all of these arrive here,
 * and every one of them is unambiguous in its first eight bytes. `useMailExtraction.ts` already
 * sniffs OLE2 this way for `.xls`; this is the same decision, made once, for both the mail path and
 * the "upload the customer's list" button.
 *
 * `doc` and `xls` share the OLE2 container and can only be told apart by their streams, which needs
 * the container open — so `sniffFileKind` reports `ole2` and the caller opens it. That is a real
 * boundary, not an omission: guessing between them from the header would be wrong half the time.
 */

/** The OLE2 compound-file signature — Word 97 `.doc` AND Excel 97 `.xls`. */
const OLE2 = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]
/** Any OOXML file — `.xlsx`, `.xlsm`, `.docx` — is a zip. */
const ZIP = [0x50, 0x4b]
const PDF = [0x25, 0x50, 0x44, 0x46]
/*
  Images (2026-09-17) — the formats Azure Document Intelligence reads: JPEG, PNG, BMP, TIFF (both
  byte orders) and HEIF/HEIC, whose `ftyp` box sits at offset 4 after the box length. A
  photographed requisition is sent there, not parsed here — see `readDocument.ts`.
*/
const JPEG = [0xff, 0xd8, 0xff]
const PNG = [0x89, 0x50, 0x4e, 0x47]
const BMP = [0x42, 0x4d]
const TIFF_LE = [0x49, 0x49, 0x2a, 0x00]
const TIFF_BE = [0x4d, 0x4d, 0x00, 0x2a]
const FTYP = [0x66, 0x74, 0x79, 0x70]

export type SniffedKind = 'ole2' | 'zip' | 'pdf' | 'image' | 'unknown'

function startsWith(bytes: Uint8Array, magic: readonly number[]): boolean {
  if (bytes.length < magic.length) return false
  return magic.every((byte, i) => bytes[i] === byte)
}

export function sniffFileKind(head: Uint8Array): SniffedKind {
  if (startsWith(head, OLE2)) return 'ole2'
  if (startsWith(head, PDF)) return 'pdf'
  if ([JPEG, PNG, BMP, TIFF_LE, TIFF_BE].some((magic) => startsWith(head, magic))) return 'image'
  if (head.length >= 12 && startsWith(head.subarray(4), FTYP)) return 'image'
  // Checked LAST of the three: a zip signature is only two bytes and the weakest of them.
  if (startsWith(head, ZIP)) return 'zip'
  return 'unknown'
}
